/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { VStack, Heading, Table, Center, Box, Flex, Spinner } from 'chakra-v3';
import { Text } from '../Text';
import { TransactionResponse } from '../../service/api/payments/Types';

interface VerifiedTransactionProps {
  transactions: TransactionResponse[];
  isLoading: boolean;
  error?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export const VerifiedTransaction: React.FC<VerifiedTransactionProps> = ({ transactions, isLoading, error }) => {
  if (isLoading) {
    return (
      <Center>
        <Flex gap={4} alignItems="center">
          <Spinner size="lg" color="spinner.blue" />
          <Text variant="body" color="text.primary">
            Loading transactions...
          </Text>
        </Flex>
      </Center>
    );
  }

  if (error) {
    return (
      <VStack gap={2} align="start">
        <Text variant="body" color="feedback.error">
          Failed to load transactions.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack gap={4} align="start" w="100%">
      <Heading as="h2">Transactions</Heading>

      {transactions.length === 0 ? (
        <Text variant="body" color="text.primary">
          No transactions found.
        </Text>
      ) : (
        <Box w="100%" overflowX="auto">
          <Table.Root size="sm" variant="outline" w="100%">
            <Table.Header bg="brand.tealDark">
              <Table.Row>
                <Table.ColumnHeader color="text.primary">Date</Table.ColumnHeader>
                <Table.ColumnHeader color="text.primary">Project</Table.ColumnHeader>
                <Table.ColumnHeader color="text.primary">Amount</Table.ColumnHeader>
                <Table.ColumnHeader color="text.primary">Status</Table.ColumnHeader>
                <Table.ColumnHeader color="text.primary">Type</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {transactions.map((tx) => (
                <Table.Row key={tx.id}>
                  <Table.Cell color="text.primary">{new Date(tx.created_at).toLocaleDateString()}</Table.Cell>
                  <Table.Cell color="text.primary">{tx.project_name}</Table.Cell>
                  <Table.Cell color="text.primary">₹{tx.amount}</Table.Cell>
                  <Table.Cell color="text.primary">{tx.status}</Table.Cell>
                  <Table.Cell color="text.primary">{tx.purpose}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VStack>
  );
};
